'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { ListChecks, ChevronDown, ChevronUp, RefreshCw, Activity, CheckCircle2, Clock, TrendingUp, TrendingDown, Plus, Brain, Zap, Archive } from 'lucide-react';
import { adaptiveProtocolService, AdaptiveProtocol, LogProtocolSessionParams } from '@/lib/services/adaptiveProtocolService';
import { useSession } from './SessionProvider';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function ImprovementBadge({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-zinc-600 text-[10px]">—</span>;
  const positive = delta >= 0;
  return (
    <span
      className="flex items-center gap-0.5 text-[10px] font-semibold"
      style={{ color: positive ? '#22C55E' : '#EF4444' }}
    >
      {positive ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {positive ? '+' : ''}{delta.toFixed(1)}
    </span>
  );
}

const URGENCY_DOT: Record<string, string> = {
  ROUTINE: '#22C55E',
  ELEVATED: '#F97316',
  CRITICAL: '#EF4444',
};

// ─── Log Session Modal ─────────────────────────────────────────────────────────
interface LogSessionModalProps {
  protocolId: string;
  onClose: () => void;
  onLogged: () => void;
}

function LogSessionModal({ protocolId, onClose, onLogged }: LogSessionModalProps) {
  const { metrics } = useSession();
  const [form, setForm] = useState({
    interventionType: 'neurotrainer' as LogProtocolSessionParams['interventionType'],
    exerciseName: '',
    difficultyLevel: '',
    durationSeconds: 0,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const id = await adaptiveProtocolService.logSession({
        protocolId,
        interventionType: form.interventionType,
        exerciseName: form.exerciseName || undefined,
        difficultyLevel: form.difficultyLevel || undefined,
        durationSeconds: form.durationSeconds,
        preAttentionScore: metrics.attentionScore,
        preCognitiveLoad: metrics.cognitiveLoadIndex,
        notes: form.notes || undefined,
      });
      if (id) {
        toast.success('Intervention logged');
        onLogged();
        onClose();
      } else {
        toast.error('Failed to log intervention');
      }
    } catch {
      toast.error('Failed to log intervention');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4,8,15,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-6"
        style={{
          background: 'linear-gradient(160deg, rgba(11,22,40,0.98), rgba(6,13,26,0.99))',
          borderColor: 'rgba(30,58,95,0.6)',
          boxShadow: '0 0 60px rgba(249,115,22,0.08)',
        }}
      >
        <h3 className="text-[15px] font-bold text-white mb-4 flex items-center gap-2">
          <Plus size={15} className="text-[#F97316]" />
          Log Intervention Session
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1 block">Type</label>
            <select
              value={form.interventionType}
              onChange={(e) => setForm({ ...form, interventionType: e.target.value as LogProtocolSessionParams['interventionType'] })}
              className="w-full bg-[#060D1A] border border-[#1E3A5F]/60 rounded-lg px-3 py-2 text-[13px] text-white outline-none"
            >
              <option value="neurotrainer">NeuroTrainer Exercise</option>
              <option value="lifestyle">Lifestyle Intervention</option>
              <option value="sleep">Sleep Protocol</option>
              <option value="followup">Clinical Follow-up</option>
            </select>
          </div>
          {form.interventionType === 'neurotrainer' && (
            <>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1 block">Exercise Name</label>
                <input
                  value={form.exerciseName}
                  onChange={(e) => setForm({ ...form, exerciseName: e.target.value })}
                  placeholder="e.g. N-Back, Pattern Shield"
                  className="w-full bg-[#060D1A] border border-[#1E3A5F]/60 rounded-lg px-3 py-2 text-[13px] text-white outline-none placeholder-zinc-700"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1 block">Difficulty</label>
                <select
                  value={form.difficultyLevel}
                  onChange={(e) => setForm({ ...form, difficultyLevel: e.target.value })}
                  className="w-full bg-[#060D1A] border border-[#1E3A5F]/60 rounded-lg px-3 py-2 text-[13px] text-white outline-none"
                >
                  <option value="">Select…</option>
                  <option value="Neuroprotect">Neuroprotect</option>
                  <option value="Rehabilitate">Rehabilitate</option>
                  <option value="Enhance">Enhance</option>
                </select>
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1 block">Duration (seconds)</label>
            <input
              type="number"
              min={0}
              value={form.durationSeconds}
              onChange={(e) => setForm({ ...form, durationSeconds: parseInt(e.target.value) || 0 })}
              className="w-full bg-[#060D1A] border border-[#1E3A5F]/60 rounded-lg px-3 py-2 text-[13px] text-white outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wide mb-1 block">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Observations, patient feedback…"
              className="w-full bg-[#060D1A] border border-[#1E3A5F]/60 rounded-lg px-3 py-2 text-[13px] text-white outline-none placeholder-zinc-700 resize-none"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-[13px] text-zinc-400 border border-[#1E3A5F]/50 hover:bg-[#0F1E35] transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-lg text-[13px] font-semibold text-white transition-all disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #F97316, #EA6C0A)' }}
            >
              {saving ? 'Saving…' : 'Log Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Protocol Card ─────────────────────────────────────────────────────────────
function ProtocolCard({ protocol, onRefresh }: { protocol: AdaptiveProtocol; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const sessions = protocol.protocolSessions ?? [];
  const totalSessions = sessions.length;
  const avgImprovement = sessions.length > 0
    ? sessions.reduce((sum, s) => sum + (s.improvementDelta ?? 0), 0) / sessions.length
    : null;

  const urgencyColor = URGENCY_DOT[protocol.prescription?.urgencyLevel ?? 'ROUTINE'];

  const handleArchive = async () => {
    setArchiving(true);
    const ok = await adaptiveProtocolService.updateStatus(protocol.id, 'archived');
    setArchiving(false);
    if (ok) { toast.success('Protocol archived'); onRefresh(); }
    else toast.error('Failed to archive');
  };

  return (
    <>
      <div
        className="rounded-xl border overflow-hidden"
        style={{
          background: 'rgba(11,22,40,0.7)',
          borderColor: protocol.status === 'archived' ? 'rgba(30,58,95,0.3)' : 'rgba(30,58,95,0.5)',
          opacity: protocol.status === 'archived' ? 0.6 : 1,
        }}
      >
        {/* Card header */}
        <div
          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#0F1E35]/50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: urgencyColor }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[12px] font-semibold text-white truncate">
                Protocol #{protocol.id.slice(0, 8)}
              </span>
              <span
                className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase"
                style={{
                  background: `${urgencyColor}20`,
                  color: urgencyColor,
                }}
              >
                {protocol.prescription?.urgencyLevel ?? 'ROUTINE'}
              </span>
              {protocol.status === 'archived' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">ARCHIVED</span>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mt-0.5">{formatDate(protocol.createdAt)}</p>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-zinc-500">
            <span className="flex items-center gap-1"><Activity size={9} />{totalSessions} sessions</span>
            {avgImprovement != null && <ImprovementBadge delta={avgImprovement} />}
          </div>
          {expanded ? <ChevronUp size={13} className="text-zinc-500 flex-shrink-0" /> : <ChevronDown size={13} className="text-zinc-500 flex-shrink-0" />}
        </div>

        {/* Expanded content */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-[#1E3A5F]/30 pt-3 space-y-3">
            {/* Biomarker snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: 'Alzheimer Risk', value: protocol.alzheimerSeverity ?? '—' },
                { label: 'ADHD Class', value: protocol.adhdClassification ?? '—' },
                { label: 'Coherence', value: protocol.avgCoherence != null ? `${protocol.avgCoherence.toFixed(0)}%` : '—' },
                { label: 'Attention', value: protocol.avgAttentionScore != null ? `${protocol.avgAttentionScore.toFixed(0)}/100` : '—' },
              ].map((m) => (
                <div key={m.label} className="bg-[#060D1A]/60 rounded-lg px-3 py-2 text-center">
                  <p className="text-[9px] text-zinc-600 mb-0.5">{m.label}</p>
                  <p className="text-[12px] font-semibold text-white">{m.value}</p>
                </div>
              ))}
            </div>

            {/* Summary */}
            {protocol.prescription?.summary && (
              <p className="text-[11px] text-zinc-400 leading-relaxed">{protocol.prescription.summary}</p>
            )}

            {/* Exercises list */}
            {protocol.prescription?.exercises && protocol.prescription.exercises.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Zap size={9} />Prescribed Exercises
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {protocol.prescription.exercises.map((ex, i) => (
                    <span
                      key={i}
                      className="text-[10px] px-2 py-1 rounded-lg border"
                      style={{
                        background: 'rgba(11,22,40,0.8)',
                        borderColor: 'rgba(30,58,95,0.5)',
                        color: '#8BAFD4',
                      }}
                    >
                      {ex.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Logged sessions */}
            {sessions.length > 0 && (
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                  <Clock size={9} />Intervention Log ({sessions.length})
                </p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {sessions.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#060D1A]/50 border border-[#1E3A5F]/30"
                    >
                      <CheckCircle2 size={10} className="text-emerald-500 flex-shrink-0" />
                      <span className="text-[11px] text-white flex-1 truncate">
                        {s.exerciseName ?? s.interventionType}
                        {s.difficultyLevel && <span className="text-zinc-500 ml-1">· {s.difficultyLevel}</span>}
                      </span>
                      <span className="text-[10px] text-zinc-600">{Math.floor(s.durationSeconds / 60)}m</span>
                      <ImprovementBadge delta={s.improvementDelta} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {protocol.status !== 'archived' && (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowLogModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#F97316] border border-[#F97316]/30 hover:bg-[#F97316]/10 transition-all"
                >
                  <Plus size={11} />Log Session
                </button>
                <button
                  onClick={handleArchive}
                  disabled={archiving}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-zinc-500 border border-[#1E3A5F]/40 hover:bg-[#0F1E35] transition-all disabled:opacity-50"
                >
                  <Archive size={11} />
                  {archiving ? 'Archiving…' : 'Archive'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showLogModal && (
        <LogSessionModal
          protocolId={protocol.id}
          onClose={() => setShowLogModal(false)}
          onLogged={onRefresh}
        />
      )}
    </>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function AdaptiveProtocolTracker() {
  const [protocols, setProtocols] = useState<AdaptiveProtocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const fetchProtocols = useCallback(async () => {
    setLoading(true);
    const data = await adaptiveProtocolService.getProtocols('anonymous', 50);
    setProtocols(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProtocols();
    const handler = () => fetchProtocols();
    window.addEventListener('protocol:saved', handler);
    return () => window.removeEventListener('protocol:saved', handler);
  }, [fetchProtocols]);

  const active = protocols.filter((p) => p.status !== 'archived');
  const archived = protocols.filter((p) => p.status === 'archived');
  const totalSessions = protocols.reduce((sum, p) => sum + (p.protocolSessions?.length ?? 0), 0);

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.95) 0%, rgba(6,13,26,0.98) 100%)',
        borderColor: 'rgba(30,58,95,0.5)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b"
        style={{ borderColor: 'rgba(30,58,95,0.4)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(74,122,181,0.2), rgba(30,58,95,0.3))' }}
          >
            <ListChecks size={16} className="text-[#4A7AB5]" />
          </div>
          <div>
            <h3 className="text-[14px] font-bold text-white">Adaptive Protocol Tracker</h3>
            <p className="text-[11px] text-zinc-500">Intervention log & progress per user</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Stats */}
          <div className="hidden sm:flex items-center gap-3 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1"><Brain size={10} />{active.length} active</span>
            <span className="flex items-center gap-1"><Activity size={10} />{totalSessions} sessions</span>
          </div>
          <button
            onClick={fetchProtocols}
            disabled={loading}
            className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-[#0F1E35] border border-[#1E3A5F]/40 transition-all disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        {loading && (
          <div className="flex items-center justify-center py-10">
            <RefreshCw size={20} className="text-[#4A7AB5] animate-spin" />
          </div>
        )}

        {!loading && protocols.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(74,122,181,0.08)', border: '1px solid rgba(74,122,181,0.15)' }}
            >
              <ListChecks size={24} className="text-[#4A7AB5]/50" />
            </div>
            <p className="text-[13px] text-zinc-400 mb-1">No protocols yet</p>
            <p className="text-[11px] text-zinc-600">Generate a prescription above and save it to start tracking</p>
          </div>
        )}

        {!loading && active.length > 0 && (
          <div className="space-y-3">
            {active.map((p) => (
              <ProtocolCard key={p.id} protocol={p} onRefresh={fetchProtocols} />
            ))}
          </div>
        )}

        {!loading && archived.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center gap-2 text-[11px] text-zinc-600 hover:text-zinc-400 transition-colors mb-2"
            >
              {showArchived ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              {archived.length} archived protocol{archived.length !== 1 ? 's' : ''}
            </button>
            {showArchived && (
              <div className="space-y-2">
                {archived.map((p) => (
                  <ProtocolCard key={p.id} protocol={p} onRefresh={fetchProtocols} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
